import React, { useEffect, useCallback, useState } from 'react';
import { useEditor } from '../../contexts/EditorContext';
import { useAuth } from '../../contexts/AuthContext';
import KonvaCanvas from './KonvaCanvas';
import EditorToolbar from './EditorToolbar';
import LayersPanel from './panels/LayersPanel';
import ElementsPanel from './panels/ElementsPanel';
import PropertiesPanel from './panels/PropertiesPanel';
import TemplatesPanel from './panels/TemplatesPanel';

const KonvaEditor = () => {
  const [leftPanel, setLeftPanel] = useState('layers');
  const { isPaidUser } = useAuth();

  const {
    slides,
    activeSlide,
    stageRef,
    deleteSelected,
    duplicateElement,
    copyElements,
    pasteElements,
    undo,
    redo,
    zoom,
    adjustZoom,
    carouselId,
    carouselData,
    saveToBackend,
    isSaving,
    lastSaved,
    saveError,
  } = useEditor();
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Delete
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteSelected();
      }
      
      // Duplicate (Ctrl+D)
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        duplicateElement();
      }

      // Copy (Ctrl+C)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        copyElements();
      }

      // Paste (Ctrl+V)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        pasteElements();
      }

      // Zoom in (Ctrl+=)
      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        adjustZoom(zoom + 0.05);
      }

      // Zoom out (Ctrl+-)
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        adjustZoom(zoom - 0.05);
      }

      // Undo (Ctrl+Z or Cmd+Z)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      
      // Redo (Ctrl+Y or Cmd+Shift+Z)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
        e.preventDefault();
        redo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [deleteSelected, duplicateElement, copyElements, pasteElements, undo, redo, zoom, adjustZoom]);
  
  // Protection for free users: disable right-click, screenshots, DevTools
  useEffect(() => {
    if (isPaidUser) return; // Paid users have no restrictions
    
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };
    
    const handleKeyDown = (e) => {
      // Block screenshot shortcuts
      if (
        (e.key === 'PrintScreen') ||
        (e.metaKey && e.shiftKey && (e.key === '3' || e.key === '4' || e.key === '5')) || // Mac screenshots
        (e.ctrlKey && e.shiftKey && e.key === 'S') // Windows Snipping Tool
      ) {
        e.preventDefault();
        alert('Screenshots are disabled for free users. Upgrade to remove restrictions!');
        return false;
      }
      
      // Block DevTools (F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U)
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };
    
    // Disable drag-and-drop to prevent image extraction
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };
    
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('dragstart', handleDragStart);
    
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, [isPaidUser]);
  
  const handleSave = useCallback(async () => {
    await saveToBackend(true); // renderAll=true — manual save always re-renders everything
  }, [saveToBackend]);
  
  const handleExport = useCallback(() => {
    if (!stageRef.current) {
      alert('Canvas not ready');
      return;
    }
    
    // Export current slide to PNG
    const dataURL = stageRef.current.toDataURL({
      mimeType: 'image/png',
      quality: 1,
      pixelRatio: 2, // High resolution
    });
    
    // Download
    const link = document.createElement('a');
    link.download = `slide-${Date.now()}.png`;
    link.href = dataURL;
    link.click();
  }, [stageRef]);
  
  // Check if this is a new carousel (has ID but no canvas_data)
  const isNewCarousel = carouselId && carouselData && !carouselData.canvas_data;
  
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Toolbar */}
      <EditorToolbar onSave={handleSave} onExport={handleExport} />
      
      {/* Info Banner for New Carousels */}
      {isNewCarousel && slides[0]?.elements.length === 0 && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              <strong>New Carousel:</strong> "{carouselData.title}" - Start by adding elements from the toolbar above. Your changes will be saved to the backend.
            </span>
          </div>
        </div>
      )}
      
      {/* Main Content: 3-column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel with Layers / Templates tabs */}
        <div className="flex flex-col border-r border-gray-200 w-48 md:w-52 lg:w-56 xl:w-64">
          {/* Tab header */}
          <div className="flex bg-white border-b border-gray-200 shrink-0">
            <button
              onClick={() => setLeftPanel('layers')}
              className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                leftPanel === 'layers'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              Layers
            </button>
            <button
              onClick={() => setLeftPanel('templates')}
              className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                leftPanel === 'templates'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              Templates
            </button>
            <button
              onClick={() => setLeftPanel('elements')}
              className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                leftPanel === 'elements'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              Elements
            </button>
          </div>
          {/* Panel content — panels own their inner height/scroll */}
          <div className="flex-1 overflow-hidden flex flex-col">
            {leftPanel === 'layers' && <LayersPanel />}
            {leftPanel === 'templates' && <TemplatesPanel />}
            {leftPanel === 'elements' && <ElementsPanel />}
          </div>
        </div>
        
        {/* Center: Canvas */}
        <div className="flex-1 flex flex-col">
          <KonvaCanvas />
          
          {/* Info Bar */}
          <div className="bg-white border-t border-gray-200 px-4 py-2 text-sm text-gray-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-gray-400">Slide</span>
                <span className="font-semibold">{activeSlide + 1} / {slides.length}</span>
                <span className="text-gray-400 ml-2">Elements:</span>
                <span className="font-semibold">{slides[activeSlide]?.elements.length || 0}</span>
              </div>

              {/* Zoom Controls */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => adjustZoom(zoom - 0.05)}
                  disabled={zoom <= 0.25}
                  className={`w-6 h-6 rounded text-sm font-bold flex items-center justify-center transition-colors ${
                    zoom <= 0.25 ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-600'
                  }`}
                  title="Zoom out (Ctrl+-)"
                >
                  −
                </button>
                <button
                  onClick={() => adjustZoom(1.0)}
                  className="px-2 py-0.5 rounded text-xs font-mono text-gray-700 hover:bg-gray-100 transition-colors min-w-12 text-center"
                  title="Reset zoom to 100%"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <button
                  onClick={() => adjustZoom(zoom + 0.05)}
                  disabled={zoom >= 2.0}
                  className={`w-6 h-6 rounded text-sm font-bold flex items-center justify-center transition-colors ${
                    zoom >= 2.0 ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-600'
                  }`}
                  title="Zoom in (Ctrl+=)"
                >
                  +
                </button>
              </div>
              
              {/* Save Status */}
              <div className="flex items-center gap-2">
                {isSaving && (
                  <span className="text-sm text-blue-600 flex items-center gap-1">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                )}
                {lastSaved && !isSaving && !saveError && (
                  <span className="text-sm text-green-600">
                    ✓ Saved {new Date(lastSaved).toLocaleTimeString()}
                  </span>
                )}
                {saveError && !isSaving && (
                  <span className="text-sm text-red-600" title={saveError}>
                    ✗ Save failed
                  </span>
                )}
              </div>
              
              <div className="text-xs text-gray-500">
                Tip: Double-click text to edit, Delete to remove
              </div>
            </div>
          </div>
        </div>
        
        {/* Right: Properties Panel */}
        <PropertiesPanel />
      </div>
    </div>
  );
};

export default KonvaEditor;
